// Seat claims: the one admission mechanism of every room, whichever backend matched its players.
// Only the edge mints claims (the Cloudflare matchmaker, the PlayFab claim endpoint); rooms and the PlayFab proxy
// verify them. A claim admits one player to one room until it expires, and names how the edge reaches that room.
// Token = base64url(JSON payload) "." base64url(HMAC-SHA-256 over the first part), keyed by NET_CLAIM_SECRET.

import { ClaimRejected, ClaimRejection, OnlineConfigError, WireFormatError } from './errors'
import { parsePlayerKey, parseRoomId, type PlayerKey, type RoomId } from './ids'
import { expectInteger, expectObject, expectOneOf, expectMatching, parseJson } from './wire'

/** How the edge reaches a room: a Durable Object it hosts, or a room server it proxies to. */
export type RoomRoute = { readonly kind: 'durable-object' } | { readonly kind: 'proxy'; readonly upstream: string }

export interface SeatClaim {
  readonly room: RoomId
  readonly player: PlayerKey
  /** Unix ms. */
  readonly expiresAt: number
  readonly route: RoomRoute
}

/** Long enough to join, drop and rejoin within one probe match (PROBE_RULES), short enough to be useless afterwards. */
export const CLAIM_TTL_MS = 5 * 60 * 1000
/** HMAC keys shorter than this are guessable. */
const MIN_SECRET_LENGTH = 32
const CLAIM_VERSION = 1
const ROUTE_KINDS = ['durable-object', 'proxy'] as const
/** host:port of a room server; hosts are DNS names or IPv4 addresses. */
const UPSTREAM_PATTERN = /^[A-Za-z0-9.-]{1,253}:[0-9]{1,5}$/
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/
const HMAC = { name: 'HMAC', hash: 'SHA-256' } as const

/** WebCrypto's key type, named the same way under the DOM, Workers and Node typings (Node has no global CryptoKey). */
type HmacKey = Awaited<ReturnType<typeof crypto.subtle.importKey>>

const utf8Encoder = new TextEncoder()
const utf8Decoder = new TextDecoder()

export class ClaimKey {
  private constructor(private readonly key: HmacKey) {}

  static async fromSecret(secret: string): Promise<ClaimKey> {
    if (secret.length < MIN_SECRET_LENGTH) throw new OnlineConfigError(`NET_CLAIM_SECRET must be at least ${MIN_SECRET_LENGTH} characters`)
    return new ClaimKey(await crypto.subtle.importKey('raw', utf8Encoder.encode(secret), HMAC, false, ['sign', 'verify']))
  }

  async sign(claim: SeatClaim): Promise<string> {
    const payload = toBase64Url(utf8Encoder.encode(JSON.stringify(encodeClaim(claim))))
    const signature = await crypto.subtle.sign(HMAC, this.key, utf8Encoder.encode(payload))
    return `${payload}.${toBase64Url(new Uint8Array(signature))}`
  }

  /** The claim a token carries, if its signature holds and it has not expired at `nowMs`. */
  async verify(token: string, nowMs: number): Promise<SeatClaim> {
    const parts = token.split('.')
    const payload = parts[0]
    const signature = parts[1]
    if (parts.length !== 2 || payload === undefined || signature === undefined) throw new ClaimRejected(ClaimRejection.malformed)
    const valid = await crypto.subtle.verify(HMAC, this.key, fromBase64Url(signature), utf8Encoder.encode(payload))
    if (!valid) throw new ClaimRejected(ClaimRejection.badSignature)
    const claim = decodeClaim(utf8Decoder.decode(fromBase64Url(payload)))
    if (claim.expiresAt <= nowMs) throw new ClaimRejected(ClaimRejection.expired)
    return claim
  }
}

/** The claim, if it was minted for rooms reached this way. */
export function requireRoute<K extends RoomRoute['kind']>(claim: SeatClaim, kind: K): SeatClaim & { readonly route: Extract<RoomRoute, { kind: K }> } {
  if (claim.route.kind !== kind) throw new ClaimRejected(ClaimRejection.wrongRoute)
  return claim as SeatClaim & { readonly route: Extract<RoomRoute, { kind: K }> }
}

export function requireRoom(claim: SeatClaim, room: RoomId): void {
  if (claim.room !== room) throw new ClaimRejected(ClaimRejection.wrongRoom)
}

function encodeClaim(claim: SeatClaim): object {
  return { v: CLAIM_VERSION, room: claim.room, player: claim.player, exp: claim.expiresAt, route: claim.route }
}

/** A signed payload is ours, so a malformed one means a key or version mismatch between edge and room. */
function decodeClaim(json: string): SeatClaim {
  try {
    const wire = expectObject(parseJson(json, 'claim'), 'claim')
    if (wire.v !== CLAIM_VERSION) throw new WireFormatError(`claim version ${String(wire.v)}`)
    return {
      room: parseRoomId(wire.room),
      player: parsePlayerKey(wire.player),
      expiresAt: expectInteger(wire.exp, 'claim expiry'),
      route: decodeRoute(wire.route),
    }
  } catch (error) {
    if (error instanceof WireFormatError) throw new ClaimRejected(ClaimRejection.malformed)
    throw error
  }
}

function decodeRoute(value: unknown): RoomRoute {
  const route = expectObject(value, 'claim route')
  const kind = expectOneOf(route.kind, ROUTE_KINDS, 'claim route kind')
  if (kind === 'durable-object') return { kind }
  return { kind, upstream: expectMatching(route.upstream, UPSTREAM_PATTERN, 'claim upstream') }
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i] as number)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  // A base64 body is never 1 mod 4 characters long; checking here keeps atob from throwing on peer input.
  if (!BASE64URL_PATTERN.test(text) || text.length % 4 === 1) throw new ClaimRejected(ClaimRejection.malformed)
  const binary = atob(text.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((text.length + 3) % 4))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}
